/**
 * @module ui/components/error-display
 * @description Error message display management
 * Handles different types of error/warning/info messages
 */

import { getElement } from '../dom/dom-manager.js';
import { logger } from '../../utils/logger.js';

/**
 * Error message types and their corresponding CSS classes
 */
export const ERROR_TYPES = {
    ERROR: 'error',
    WARNING: 'warning',
    INFO: 'info',
    SUCCESS: 'success'
};

/**
 * Show an error/warning/info message
 * @param {string} message - Message to display
 * @param {string} [type='error'] - Message type (error, warning, info, success)
 * @param {string} [targetElementId='error-message'] - ID of element to display message in
 */
export function showError(message, type = ERROR_TYPES.ERROR, targetElementId = 'error-message') {
    let errorElement;

    // Try to get element from DOM manager first
    if (targetElementId === 'error-message') {
        errorElement = getElement('errorMessage');
    }

    // Fallback to direct DOM query if not in manager
    if (!errorElement) {
        errorElement = document.getElementById(targetElementId);
    }

    if (!errorElement) {
        logger.warn(`Error display element "${targetElementId}" not found`);
        logger.error(message); // Fallback to logger
        return;
    }

    // Set message content
    errorElement.textContent = message;

    // Remove any existing type classes
    Object.values(ERROR_TYPES).forEach(t => {
        errorElement.classList.remove(t);
    });

    // Add the specified type class
    if (Object.values(ERROR_TYPES).includes(type)) {
        errorElement.classList.add(type);
    }

    // Show the element
    errorElement.classList.remove('hidden');

    // Add ARIA attributes for accessibility
    errorElement.setAttribute('role', type === ERROR_TYPES.ERROR ? 'alert' : 'status');
    errorElement.setAttribute('aria-live', type === ERROR_TYPES.ERROR ? 'assertive' : 'polite');
}

/**
 * Hide error message
 * @param {string} [targetElementId='error-message'] - ID of element to hide
 */
export function hideError(targetElementId = 'error-message') {
    let errorElement;

    // Try to get element from DOM manager first
    if (targetElementId === 'error-message') {
        errorElement = getElement('errorMessage');
    }

    // Fallback to direct DOM query if not in manager
    if (!errorElement) {
        errorElement = document.getElementById(targetElementId);
    }

    if (!errorElement) {
        return;
    }

    errorElement.classList.add('hidden');
    errorElement.textContent = '';

    // Remove type classes
    Object.values(ERROR_TYPES).forEach(t => {
        errorElement.classList.remove(t);
    });

    // Remove ARIA attributes
    errorElement.removeAttribute('role');
    errorElement.removeAttribute('aria-live');
}

/**
 * Show project/entity-specific error
 * @param {string} message - Error message
 */
export function showProjectError(message) {
    showError(message, ERROR_TYPES.ERROR, 'project-error');
}

/**
 * Hide project/entity-specific error
 */
export function hideProjectError() {
    hideError('project-error');
}

/**
 * Show upload-specific error
 * @param {string} message - Error message
 */
export function showUploadError(message) {
    showError(message, ERROR_TYPES.ERROR, 'upload-error');
}

/**
 * Hide upload-specific error
 */
export function hideUploadError() {
    hideError('upload-error');
}

/**
 * Show a temporary error that auto-hides after a delay
 * @param {string} message - Message to display
 * @param {number} [duration=5000] - Duration in milliseconds before auto-hide
 * @param {string} [type='error'] - Message type
 * @param {string} [targetElementId='error-message'] - Target element ID
 * @returns {number} Timeout ID (can be used with clearTimeout to cancel)
 */
export function showTemporaryError(message, duration = 5000, type = ERROR_TYPES.ERROR, targetElementId = 'error-message') {
    showError(message, type, targetElementId);

    return setTimeout(() => {
        hideError(targetElementId);
    }, duration);
}

/**
 * Show a success message
 * @param {string} message - Success message to display
 * @param {string} [targetElementId='error-message'] - Target element ID
 */
export function showSuccess(message, targetElementId = 'error-message') {
    showError(message, ERROR_TYPES.SUCCESS, targetElementId);
}

/**
 * Show a warning message
 * @param {string} message - Warning message to display
 * @param {string} [targetElementId='error-message'] - Target element ID
 */
export function showWarning(message, targetElementId = 'error-message') {
    showError(message, ERROR_TYPES.WARNING, targetElementId);
}

/**
 * Show an info message
 * @param {string} message - Info message to display
 * @param {string} [targetElementId='error-message'] - Target element ID
 */
export function showInfo(message, targetElementId = 'error-message') {
    showError(message, ERROR_TYPES.INFO, targetElementId);
}

/**
 * Create an error display manager for a specific element
 * @param {string} elementId - ID of the error display element
 * @returns {Object} Object with show/hide methods bound to the specific element
 * @example
 * const uploadErrors = createErrorScope('upload-error');
 * uploadErrors.show('Upload failed');
 * uploadErrors.hide();
 */
export function createErrorScope(elementId) {
    return {
        show(message, type = ERROR_TYPES.ERROR) {
            showError(message, type, elementId);
        },
        hide() {
            hideError(elementId);
        },
        showTemporary(message, duration = 5000, type = ERROR_TYPES.ERROR) {
            return showTemporaryError(message, duration, type, elementId);
        },
        showSuccess(message) {
            showError(message, ERROR_TYPES.SUCCESS, elementId);
        },
        showWarning(message) {
            showError(message, ERROR_TYPES.WARNING, elementId);
        },
        showInfo(message) {
            showError(message, ERROR_TYPES.INFO, elementId);
        }
    };
}

export default {
    showError,
    hideError,
    showProjectError,
    hideProjectError,
    showUploadError,
    hideUploadError,
    showTemporaryError,
    showSuccess,
    showWarning,
    showInfo,
    createErrorScope,
    ERROR_TYPES
};
